import { type FC } from "react";

import { DEFAULT_CHEQUE_MINIMUM_AMOUNT, getCurrencyFromCountry, TicketingFormCategory } from "@simplyk/common";
import { UseFormReturn, useWatch } from "react-hook-form";

import { AmplitudeEvents } from "../../../../../common/constants/amplitude";
import { useCurrentUserContext } from "../../../../../common/contexts/CurrentUserContext";
import { useLocaleContext } from "../../../../../common/contexts/LocaleContext";
import { isMin, isRequired } from "../../../../../common/helpers/validators";

import { useTranslate } from "@/common/hooks/useTranslate";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LiveEditorToggle } from "@/components/LiveEditorElements/LiveEditorToggle/LiveEditorToggle";
import { DashboardAmountInput } from "@/dashboard-design-system/DashboardAmountInput";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import type { StepAdvancedSettingsFormData } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/types";

export type PaymentMethod = "cheque" | "cash";

type OfflinePaymentMethodCardProps = {
  form: UseFormReturn<StepAdvancedSettingsFormData>;
};

interface MethodConfig {
  enabledField: keyof StepAdvancedSettingsFormData;
  descriptionField: keyof StepAdvancedSettingsFormData;
  switchLabel: string;
  textFieldLabel: string;
  defaultWording: string;
  amplitudeEvent?: {
    enabled: AmplitudeEvents;
    disabled: AmplitudeEvents;
  };
}

export const OfflinePaymentMethodCard: FC<OfflinePaymentMethodCardProps> = ({ form }) => {
  const { t } = useTranslate();
  const { organization } = useCurrentUserContext();
  const { locale } = useLocaleContext();
  const { formCategory } = useLiveFormEditorContext();
  const { logUserActionEvent, logUserInputEvent } = useLiveFormAmplitudeEvents();

  const {
    control,
    formState: { errors },
  } = form;

  // Determine payment method based on formCategory
  const isAuction = formCategory === TicketingFormCategory.Auction;
  const method: PaymentMethod = isAuction ? "cash" : "cheque";

  const methodConfigs = {
    cheque: {
      enabledField: "chequeEnabled",
      descriptionField: "chequeDescription",
      switchLabel: "donationFormAndTicketing.advancedOptions.chequeEnabled",
      textFieldLabel: "donationForm.chequeDescription",
      defaultWording: "chequeDescription.placeholder",
      amplitudeEvent: {
        enabled: AmplitudeEvents.FormChequePaymentAdded,
        disabled: AmplitudeEvents.FormChequePaymentRemoved,
      },
    },
    cash: {
      enabledField: "chequeEnabled", // Using the same field for backward compatibility
      descriptionField: "chequeDescription", // Using the same field for backward compatibility
      switchLabel: "liveFormEditor.offlinePaymentCard.chequeEnabled",
      textFieldLabel: "liveFormEditor.offlinePaymentCard.offlinePaymentWording.label",
      defaultWording: "liveFormEditor.offlinePaymentCard.offlinePaymentWording.default",
      amplitudeEvent: {
        enabled: AmplitudeEvents.FormChequePaymentAdded,
        disabled: AmplitudeEvents.FormChequePaymentRemoved,
      },
    },
  } as const satisfies Record<PaymentMethod, MethodConfig>;

  const methodConfig = methodConfigs[method];
  const isEnabled = useWatch({ control, name: methodConfig.enabledField });
  const currency = getCurrencyFromCountry(organization?.country);

  return (
    <LiveEditorBlock rowGap={1}>
      <LiveEditorToggle
        name={methodConfig.enabledField}
        label={t("dashboard", methodConfig.switchLabel)}
        labelVariant="body1"
        control={form.control}
        onValueChange={(checked) => {
          form.setValue(methodConfig.descriptionField, checked ? t("dashboard", methodConfig.defaultWording) : "", {
            shouldDirty: true,
          });
          if (method === "cheque") {
            form.clearErrors("chequeMinimumAmount");
            if (checked && !form.getValues("chequeMinimumAmount")) {
              form.setValue("chequeMinimumAmount", DEFAULT_CHEQUE_MINIMUM_AMOUNT, { shouldDirty: true });
            }
          }
          if (methodConfig.amplitudeEvent) {
            logUserActionEvent(checked ? methodConfig.amplitudeEvent.enabled : methodConfig.amplitudeEvent.disabled);
          }
        }}
      />

      {isEnabled && method === "cheque" && (
        <>
          <DashboardTypography variant="body2">
            {t("dashboard", "donationFormAndTicketing.advancedOptions.chequeMinimumAmount")}
          </DashboardTypography>
          <DashboardAmountInput
            name="chequeMinimumAmount"
            control={control}
            locale={locale}
            currency={currency}
            error={Boolean(errors["chequeMinimumAmount"]?.message)}
            errorMessage={errors["chequeMinimumAmount"]?.message}
            rules={[
              isRequired(t("dashboard", "donationFormAndTicketing.advancedOptions.chequeMinimumAmountError")),
              isMin(100, t("dashboard", "donationFormAndTicketing.advancedOptions.chequeMinimumAmountError")),
            ]}
            onBlur={logUserInputEvent({
              inputName: "chequeMinimumAmount",
              isEmpty: !form.getValues("chequeMinimumAmount"),
              isValid: !errors["chequeMinimumAmount"],
            })}
            sx={{ width: "fit-content" }}
            defaultValue={DEFAULT_CHEQUE_MINIMUM_AMOUNT}
          />
        </>
      )}

      {isEnabled && (
        <DashboardTextField
          multiline={method === "cheque"}
          rows={method === "cheque" ? 4 : undefined}
          maxRows={method === "cheque" ? 8 : undefined}
          inputProps={method === "cash" ? { maxLength: 35 } : undefined}
          name={methodConfig.descriptionField}
          label={t("dashboard", methodConfig.textFieldLabel)}
          control={form.control}
          rules={[isRequired(!!isEnabled, t("common", "isRequired"))]}
          errorMessage={errors[methodConfig.descriptionField]?.message}
          onBlur={logUserInputEvent({
            inputName: methodConfig.descriptionField,
            isEmpty: !form.getValues(methodConfig.descriptionField)?.toString?.().trim(),
            isValid: !errors[methodConfig.descriptionField],
          })}
        />
      )}
    </LiveEditorBlock>
  );
};
